import * as React from 'react';
import { cn } from '../utils';

const Timeline = React.forwardRef<HTMLOListElement, React.HTMLAttributes<HTMLOListElement>>(
  ({ className, ...props }, ref) => (
    <ol ref={ref} className={cn('relative border-l border-border ml-2', className)} {...props} />
  ),
);
Timeline.displayName = 'Timeline';

const TimelineItem = React.forwardRef<HTMLLIElement, React.HTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn('relative mb-6 ml-4', className)} {...props} />
  ),
);
TimelineItem.displayName = 'TimelineItem';

const TimelineDot = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'destructive';
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'absolute -left-[1.5rem] top-0 mt-1.5 h-4 w-4 rounded-full border-2 z-10',
      {
        'border-border': variant === 'default',
        'border-primary bg-primary': variant === 'primary',
        'border-secondary bg-secondary': variant === 'secondary',
        'border-green-500 bg-green-500': variant === 'success',
        'border-yellow-500 bg-yellow-500': variant === 'warning',
        'border-destructive bg-destructive': variant === 'destructive',
      },
      className,
    )}
    {...props}
  />
));
TimelineDot.displayName = 'TimelineDot';

const TimelineContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mb-2 text-sm', className)} {...props} />
  ),
);
TimelineContent.displayName = 'TimelineContent';

const TimelineHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mb-1 flex items-center gap-2', className)} {...props} />
  ),
);
TimelineHeader.displayName = 'TimelineHeader';

const TimelineTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h3 ref={ref} className={cn('font-semibold leading-none tracking-tight', className)} {...props}>
    {children ?? <span className='sr-only'>Timeline title</span>}
  </h3>
));
TimelineTitle.displayName = 'TimelineTitle';

const TimelineTime = React.forwardRef<HTMLTimeElement, React.HTMLAttributes<HTMLTimeElement>>(
  ({ className, ...props }, ref) => (
    <time ref={ref} className={cn('text-xs text-muted-foreground', className)} {...props} />
  ),
);
TimelineTime.displayName = 'TimelineTime';

const TimelineDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-muted-foreground', className)} {...props} />
));
TimelineDescription.displayName = 'TimelineDescription';

export {
  Timeline,
  TimelineContent,
  TimelineDescription,
  TimelineDot,
  TimelineHeader,
  TimelineItem,
  TimelineTime,
  TimelineTitle,
};
